// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useParams } from 'react-router-dom';
import { useFloorPlan } from '../../hooks/useAlarms';
import { tempBand } from '../../mocks/floorTemps';
import type { EquipmentStatus } from '../../types/alarm';
import { PageHead, EmptyState, Loading } from '../../components/shell/ScreenHelpers';
import './FloorPlanScreen.css';

function crahColor(s: EquipmentStatus) {
  return s === 'fault'
    ? 'var(--st-fault)'
    : s === 'warn'
      ? 'var(--st-warn)'
      : s === 'idle'
        ? 'var(--st-idle)'
        : 'var(--st-ok)';
}

const LEGEND: { label: string; band: string }[] = [
  { label: '<68° cool', band: 'tcool' },
  { label: '68–75°', band: 'tok' },
  { label: '76–83°', band: 'twarn' },
  { label: '84–89°', band: 'thot' },
  { label: '≥90°', band: 'tcrit' },
];

export default function FloorPlanScreen() {
  const { site_id } = useParams();
  const { floorPlan, loading } = useFloorPlan(site_id ?? '');

  if (loading) return <Loading />;

  if (!floorPlan) {
    return (
      <div className="hs-page">
        <PageHead title="Floor plan" dateline="Physical layout · per-rack inlet temps °F" />
        <EmptyState
          big="No floor plan for this site."
          sub="Floor temps are only mocked for GA-10 (atl) in this PR."
        />
      </div>
    );
  }

  return (
    <div className="hs-page">
      <PageHead title="Floor plan" dateline="Physical layout · per-rack inlet temps °F" />

      {floorPlan.note && <p className="fp-note">{floorPlan.note}</p>}

      <div className="fp-rooms">
        {floorPlan.rooms.map(r => {
          const band = tempBand(r.temp);
          return (
            <div key={r.name} className="fp-room">
              <span className="fp-room-name">{r.name}</span>
              <span className="fp-room-temp" style={{ color: `var(--${band})` }}>
                {r.temp}°F
              </span>
            </div>
          );
        })}
      </div>

      {floorPlan.halls.map(hall => (
        <section key={hall.id} className="fp-hall">
          <div className="fp-hall-head">
            <div className="fp-hall-name">{hall.name}</div>
            <div className="fp-crah-list">
              {hall.crah.map(c => (
                <span key={c.label} className="fp-crah">
                  <span className="dot" style={{ background: crahColor(c.status) }} />
                  {c.label}
                </span>
              ))}
            </div>
          </div>

          {hall.rows.map(row => (
            <div key={row.label} className="fp-row">
              <div className="fp-row-label">{row.label}</div>
              <div className="fp-racks">
                {row.cells.map(cell => {
                  const band = tempBand(cell.temp);
                  return (
                    <div
                      key={cell.id}
                      className="fp-rack"
                      style={{
                        background: `var(--${band}bg)`,
                        border: `1px solid var(--${band}bd)`,
                      }}
                    >
                      <div className="fp-rack-id">{cell.id}</div>
                      <div className="fp-rack-temp" style={{ color: `var(--${band})` }}>
                        {cell.temp}°F
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>
      ))}

      <div className="fp-legend">
        {LEGEND.map(l => (
          <span key={l.band} className="fp-legend-item">
            <span className="swatch" style={{ background: `var(--${l.band})` }} />
            {l.label}
          </span>
        ))}
      </div>

      {floorPlan.footer && <p className="fp-footer">{floorPlan.footer}</p>}
    </div>
  );
}
